import { Checkbox, Radio } from '@sly/icons/react';
import cx from 'classnames';
import { array, bool, func, node, oneOf, oneOfType, string } from 'proptypes';
import React, { forwardRef, useCallback } from 'react';

const Label = forwardRef(({ children, className }, ref) => (
  <div
    className={cx(
      'grow md:order-1 md:ml-m md:mr-[unset]',
      className ? className : ''
    )}
    ref={ref}
    data-tp-id='gen-div-cc43fdc6-8b48-42f1-8993-bc47d820c1ae'
  >
    {children}
  </div>
));

Label.propTypes = {
  children: node,
};

const CheckboxRow = ({ checked, label, ...props }) => (
  <div
    className='filterChoiceRow mb-xs h-[2.5rem]'
    {...props}
    data-tp-id='gen-div-640a18fa-a0df-4611-82f5-1505658667f6'
  >
    <Label className='font-b-m lg:cursor-pointer'>{label}</Label>
    <Checkbox
      className='icon-style min-w-[24px]'
      color='text-viridian-base'
      active={checked}
      size='m'
    />
  </div>
);

CheckboxRow.propTypes = {
  checked: bool,
  label: string,
};

const RadioRow = ({ label, description, checked, ...props }) => (
  <div
    className='filterChoiceRow mb-l'
    {...props}
    data-tp-id='gen-div-07610d8c-f736-4d90-b406-e329bbd89f52'
  >
    <Label className='text-slate-lighter-30 font-b-m lg:cursor-pointer'>
      <div
        className={cx('text-slate-base', !description ? 'mb-0' : 'mb-xxs')}
        data-tp-id='gen-div-d9c21893-053e-4b75-b95a-eca8b12c7487'
      >
        {label}
      </div>
      <span
        className='text-slate-lighter-40'
        data-tp-id='gen-span-70532a47-94ed-4387-8ffe-9e2bf5d08440'
      >
        {description}
      </span>
    </Label>
    <Radio
      className='icon-style min-w-[24px]'
      size='m'
      color='text-viridian-base'
      active={checked}
    />
  </div>
);

RadioRow.propTypes = {
  type: oneOf(['checkbox', 'radio']),
  checked: bool,
  label: string,
  description: string,
};

const FilterChoice = ({ type, filter, options, onChange, value = [] }) => {
  if (!Array.isArray(value)) {
    value = [value];
  }

  const change = useCallback(
    (checked, optionValue) => {
      if (checked) {
        onChange(
          filter,
          value.filter((x) => x !== optionValue)
        );
      } else {
        const currentValue = type === 'radio' ? [] : [...value];
        onChange(filter, [...currentValue, optionValue]);
      }
    },
    [value, onChange]
  );

  return (
    <>
      {options.map(({ label, value: optionValue, description }) => {
        const Row = type === 'checkbox' ? CheckboxRow : RadioRow;
        const checked = value.includes(optionValue);
        return (
          <Row
            key={optionValue}
            onClick={() => change(checked, optionValue)}
            checked={checked}
            label={label}
            description={description}
          />
        );
      })}
    </>
  );
};

FilterChoice.propTypes = {
  type: oneOf(['checkbox', 'radio']).isRequired,
  filter: string.isRequired,
  options: array,
  onChange: func,
  value: oneOfType([string, array]),
};

export default FilterChoice;
